import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button.js';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../ui/dialog.js';
import { Drawer, DrawerTrigger, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter, DrawerClose } from '../ui/drawer.js';
import { Images } from '../../assets/ui/images/icons/index.js';
import { useDevice } from '../../contexts/DeviceContext.js';
import { useBodyScrollLock } from '../../hooks/use-body-lock.js';
import { useKeyboardScroll } from '../../hooks/use-keyboard-scroll.js';
import { cn } from '../../lib/utils.js';
import { LineIcon } from '../ui/icons/LineIcon.js';
import { ButtonProgress } from './button-progress.js';

const DialogWrapper = ({
  open,
  setOpen,
  trigger,
  title = "",
  children,
  isFull = false,
  footerRender,
  footerClass = "",
  footerBottom,
  footer = [],
  onClose,
  hiddenFooter,
  className = "",
  checkScroll = false,
  enableLargeText = false,
  screenWfull,
  dismissible = true,
  onCloseAutoFocus,
  headerClass
}) => {
  const { isDesktop, isLargeText, isPopupScroll, device, isIos } = useDevice();
  const contentRef = useRef(null);
  const [percent, setPercent] = useState(0);
  const [innerOpen, setInnerOpen] = useState(open);
  const prevInnerOpenRef = useRef(innerOpen);
  const drawerRef = useRef(null);
  const { isKeyboardOpen } = useKeyboardScroll();
  useBodyScrollLock(!!(innerOpen && isIos && !isDesktop));
  console.log("dismissible : ", dismissible);
  useEffect(() => {
    setInnerOpen(open);
  }, [open]);
  useEffect(() => {
    if (prevInnerOpenRef.current === innerOpen) {
      return;
    }
    prevInnerOpenRef.current = innerOpen;
    window.dispatchEvent(
      new CustomEvent("dialog:visibility-change", {
        detail: {
          open: innerOpen
        }
      })
    );
  }, [innerOpen]);
  const handleClose = useCallback(() => {
    console.log("닫기");
    setInnerOpen(false);
    onClose?.();
  }, [setInnerOpen, onClose]);
  const onOpenChange = useCallback(
    (open2) => {
      setInnerOpen(open2);
      setTimeout(() => {
        setOpen?.(open2);
      }, 150);
    },
    [setOpen, setInnerOpen]
  );
  const handleScroll = () => {
    console.log("ok");
    if (!contentRef?.current) return;
    const outerHeight = contentRef?.current?.offsetHeight;
    const scrollHeight = contentRef?.current?.scrollHeight;
    const gapHeight = scrollHeight - outerHeight;
    const scrollPos = contentRef?.current?.scrollTop;
    const percent2 = Math.ceil(scrollPos / gapHeight * 100);
    setPercent(scrollPos >= gapHeight - 20 ? 100 : percent2);
  };
  const scrollByPage = () => {
    const el = contentRef?.current;
    if (!el) return;
    const style = getComputedStyle(el);
    const step = el.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    const top = el.scrollTop + step;
    if ("scrollBehavior" in document.documentElement.style) {
      el.scrollTo({ top, behavior: "smooth" });
    } else {
      el.scrollTop = top;
    }
  };
  const applied = enableLargeText && isLargeText && device !== "desktop";
  if (isDesktop) {
    return /* @__PURE__ */ jsxs(Dialog, { open: innerOpen, onOpenChange, children: [
      trigger && /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: trigger }),
      /* @__PURE__ */ jsxs(
        DialogContent,
        {
          onInteractOutside: (e) => e.preventDefault(),
          className: cn(
            "dialog-content",
            `${isFull ? "w-[600px] max-h-[628px]" : "w-[480px] max-h-[628px] "}`,
            "has-[.is-footer]:flex has-[.is-footer]:flex-col",
            className
          ),
          "data-text-display": enableLargeText && isLargeText && !isDesktop,
          onCloseAutoFocus,
          children: [
            /* @__PURE__ */ jsx(
              DialogHeader,
              {
                className: cn("w-full flex items-center shrink-0 h-[58px]", headerClass),
                children: title && /* @__PURE__ */ jsx(DialogTitle, { className: "flex items-center", children: title })
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                ref: contentRef,
                onScroll: checkScroll ? handleScroll : void 0,
                className: cn(
                  "p-8 max-h-[490px] h-auto overflow-auto pb-[32px] w-full flex flex-col justify-between dialog-content",
                  isFull ? "max-h-[546px]" : "max-h-[490px]",
                  "has-[.is-footer]:pb-0 has-[.is-footer]:px-0 not-[.is-footer]:pb-0",
                  "has-[.draw-fixed-layout]:max-h-[570px] has-[.draw-fixed-layout]:min-h-[372px]"
                ),
                children: /* @__PURE__ */ jsx("div", { className: cn(footer.length > 0 && !hiddenFooter), children })
              }
            ),
            footer.length > 0 && // 클래스 추가 sticky
            /* @__PURE__ */ jsxs(
              DialogFooter,
              {
                className: cn("is-footer footer-wrap sticky shrink-0", footerClass),
                children: [
                  /* @__PURE__ */ jsx("div", { className: "absolute -top-4 left-0 right-0 h-4 bg-gradient-to-t from-white via-white/70 to-transparent backdrop-blur-sm" }),
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: cn(
                        "p-8 backdrop:bg-transparent-black-16",
                        footerRender && "pt-4"
                      ),
                      children: [
                        footerRender && /* @__PURE__ */ jsx("div", { className: "pb-10", children: footerRender }),
                        /* @__PURE__ */ jsx("div", { className: " w-full inline-flex flex-grow gap-2", children: footer.map((btn, idx) => {
                          return btn.hasClose ? /* @__PURE__ */ jsx(DialogClose, { asChild: true, children: /* @__PURE__ */ jsx(
                            Button,
                            {
                              className: cn(
                                "w-full flex-1 !outline-offset-4",
                                btn.hideDesktop ? "hidden" : ""
                              ),
                              size: btn.type === "text" ? "xsm" : "lg",
                              onClick: (e) => btn.handler(e),
                              variant: btn.type === "text" ? "textLink" : btn.type === "secondary" ? "scOutline" : "default",
                              disabled: btn.disabled,
                              children: btn.title
                            }
                          ) }, `dialog-close-${idx}`) : btn.type === "scroll" ? /* @__PURE__ */ jsx(
                            ButtonProgress,
                            {
                              percentage: percent,
                              remainingText: btn.title ?? "스크롤해서 끝까지 읽어주세요",
                              completeText: btn.endTitle ?? "완료",
                              onClick: percent >= 100 ? btn.handler : scrollByPage,
                              className: "disabled:!border-primary-03"
                            },
                            `dialog-btn-${idx}`
                          ) : /* @__PURE__ */ jsx(
                            Button,
                            {
                              className: cn(
                                "w-full flex-1",
                                btn.hideDesktop ? "hidden" : ""
                              ),
                              size: btn.type === "text" ? "xsm" : "lg",
                              onClick: (e) => btn.handler(e),
                              variant: btn.type === "text" ? "textLink" : btn.type === "secondary" ? "scOutline" : "default",
                              disabled: btn.disabled,
                              ref: btn.btnRef,
                              children: btn.title
                            },
                            `dialog-btn-${idx}`
                          );
                        }) })
                      ]
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx(DialogClose, { asChild: true, className: "absolute top-[17px] right-[24px]", children: /* @__PURE__ */ jsx("button", { onClick: handleClose, children: /* @__PURE__ */ jsx("img", { src: Images.Close, alt: "닫기" }) }) })
          ]
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs(
    Drawer,
    {
      open: innerOpen,
      onOpenChange,
      repositionInputs: false,
      dismissible,
      children: [
        /* @__PURE__ */ jsx(DrawerTrigger, { asChild: true, children: trigger }),
        /* @__PURE__ */ jsxs(
          DrawerContent,
          {
            className: cn(
              isFull && "h-[calc(100dvh-(var(--safe-area-inset-top)))]",
              !isFull && "max-h-[calc(100dvh-(90px+(var(--safe-area-inset-top))))] md:max-h-[628px]",
              isPopupScroll ? "overflow-y-auto pb-[35vh]" : "",
              className
            ),
            "data-text-display": String(applied),
            ref: drawerRef,
            children: [
              /* @__PURE__ */ jsx(DrawerHeader, { className: headerClass, children: /* @__PURE__ */ jsx(DrawerTitle, { children: title }) }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  ref: contentRef,
                  onScroll: checkScroll ? handleScroll : void 0,
                  className: cn(
                    "dialog-viewport",
                    "pb-8",
                    "px-8 pt-8 has-[.is-footer]:px-0 has-[.is-footer]:pb-0 overflow-y-auto flex-1 flex flex-col justify-between min-h-0",
                    screenWfull && "px-0 py-0"
                  ),
                  children: /* @__PURE__ */ jsx(
                    "div",
                    {
                      className: cn(
                        footerRender && "pb-[200px]",
                        "has-[.share-group]:pb-[30px]",
                        "has-[.mobile-id-group]:pb-[0px]",
                        "has-[.no-padding]:pb-[0px] has-[.no-padding]:pt-[0px]"
                      ),
                      children
                    }
                  )
                }
              ),
              footer.length > 0 && !hiddenFooter && // 클래스 추가 sticky
              /* @__PURE__ */ jsx(
                DrawerFooter,
                {
                  className: cn(
                    "footer-wrap is-footer z-20 ",
                    footerClass,
                    isKeyboardOpen ? "is-keyboard-open" : ""
                  ),
                  style: footerRender ? { boxShadow: "rgba(255, 255, 255, .8) 0px -9px 20px 0px" } : void 0,
                  children: /* @__PURE__ */ jsxs(
                    "div",
                    {
                      className: cn(
                        "pt-6 pb-8 px-6 w-full",
                        footerRender && "pt-5",
                        footerBottom && "pb-6",
                        "has-[.btn-link]:pt-0"
                      ),
                      children: [
                        footerRender && /* @__PURE__ */ jsx("div", { className: "pb-10", children: footerRender }),
                        /* @__PURE__ */ jsx("div", { className: "w-full inline-flex flex-grow gap-2", children: footer.map((btn, idx) => {
                          return btn.hasClose ? /* @__PURE__ */ jsx(DrawerClose, { asChild: true, children: /* @__PURE__ */ jsx(
                            Button,
                            {
                              className: "w-full flex-1",
                              size: btn.type === "text" ? "xsm" : "lg",
                              onClick: (e) => {
                                btn.handler(e);
                              },
                              variant: btn.type === "text" ? "textLink" : btn.type === "secondary" ? "scOutline" : "default",
                              children: btn.title
                            }
                          ) }, `drawer-close-${idx}`) : btn.type === "scroll" ? /* @__PURE__ */ jsx(
                            ButtonProgress,
                            {
                              percentage: percent,
                              remainingText: btn.title ?? "스크롤해서 끝까지 읽어주세요",
                              completeText: btn.endTitle ?? "완료",
                              onClick: percent >= 100 ? btn.handler : scrollByPage
                            },
                            `drawer-btn-${idx}`
                          ) : /* @__PURE__ */ jsx(
                            Button,
                            {
                              className: cn(
                                "w-full flex-1",
                                btn.type === "text" && "bottom-text-btn"
                              ),
                              size: btn.type === "text" ? "xsm" : "lg",
                              onClick: (e) => btn.handler(e),
                              variant: btn.type === "text" ? "textLink" : btn.type === "secondary" ? "scOutline" : "default",
                              disabled: btn.disabled,
                              children: btn.title
                            },
                            `drawer-btn-${idx}`
                          );
                        }) }),
                        footerBottom && /* @__PURE__ */ jsx(Fragment, { children: footerBottom })
                      ]
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx(DrawerClose, { asChild: true, className: "absolute top-4 right-6", children: /* @__PURE__ */ jsx("button", { onClick: handleClose, "aria-label": "팝업 닫기", children: /* @__PURE__ */ jsx(LineIcon, { icoColor: "bg-icon-base-black", icoName: "ico-close" }) }) })
            ]
          }
        )
      ]
    }
  );
};

export { DialogClose, DialogWrapper };
